import {
  EntityAutocompletePicker,
  type DefaultEntityFilters,
} from '@backstage/plugin-catalog-react';
import {
  EntityUserCountryFilter,
  EntityUserDepartmentFilter,
  EntityUserOfficeFilter,
  EntityUserRoleFilter,
} from './userAnnotationFilters';

/**
 * Thin wrappers around EntityAutocompletePicker — Backstage's reusable
 * filter primitive. Each picker:
 *
 *   - Calls `catalogApi.getEntityFacets` for distinct values on its path
 *   - Auto-hides when no entities in the loaded scope have a value (so
 *     these don't appear on kind=Component or other irrelevant pages)
 *   - Multi-select with count display
 *
 * Mirrors the stock EntityTagPicker shape, with one extra step: the
 * picker's `name` prop is constrained to keys of `AllowedEntityFilters`,
 * and our four names (`user-role`, `user-department`, `user-office`,
 * `user-country`) aren't in the stock `DefaultEntityFilters` type. So
 * we extend the type locally and pass it as the generic.
 */
type AppEntityFilters = DefaultEntityFilters & {
  'user-role'?: InstanceType<typeof EntityUserRoleFilter>;
  'user-department'?: InstanceType<typeof EntityUserDepartmentFilter>;
  'user-office'?: InstanceType<typeof EntityUserOfficeFilter>;
  'user-country'?: InstanceType<typeof EntityUserCountryFilter>;
};

export const EntityUserRolePicker = () => (
  <EntityAutocompletePicker<AppEntityFilters, 'user-role'>
    label="Role"
    name="user-role"
    path="metadata.annotations.user/role"
    Filter={EntityUserRoleFilter}
    showCounts
  />
);

export const EntityUserDepartmentPicker = () => (
  <EntityAutocompletePicker<AppEntityFilters, 'user-department'>
    label="Department"
    name="user-department"
    path="metadata.annotations.user/department"
    Filter={EntityUserDepartmentFilter}
    showCounts
  />
);

export const EntityUserOfficePicker = () => (
  <EntityAutocompletePicker<AppEntityFilters, 'user-office'>
    label="Office"
    name="user-office"
    path="metadata.annotations.user/office"
    Filter={EntityUserOfficeFilter}
    showCounts
  />
);

export const EntityUserCountryPicker = () => (
  <EntityAutocompletePicker<AppEntityFilters, 'user-country'>
    label="Country"
    name="user-country"
    path="metadata.annotations.user/country"
    Filter={EntityUserCountryFilter}
    showCounts
  />
);
